/**
 * Auth Store (Zustand)
 * 
 * Feature: 033-auth-complete
 * Description: Global authentication state management
 * Constitution: v1.11.0
 */

import { create } from 'zustand';
import { User, PermissionsResponse } from '@shared/types/auth';
import {
  NO_LICENSES, UNVERIFIABLE_LICENSE, type PlugReason, type SessionLicense, type TenantLicenses,
} from '@shared/access/licenses';
import { roleSatisfies, toRole, type Role } from '@shared/access/roles';

/** Spec 286 US4 — a licence refusal answered by a later API call (403 LICENSE_INACTIVE / 503 LICENSE_UNVERIFIABLE). */
export type LicenceRefusal = { state: 'plugged'; plugReason: PlugReason } | { state: 'unverifiable' };

interface AuthState {
  // State
  user: User | null;
  permissions: PermissionsResponse | null;
  /** Spec 285 — the viewing tenant's resolved licenses (SuperAdmin → all true). */
  licenses: TenantLicenses;
  /**
   * Spec 286 — the session's resolved licence; `null` until the session is read (or from a backend that
   * does not send it), which renders the app as before. A plugged or unverifiable state replaces the app.
   */
  license: SessionLicense | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  lastActivityAt: number;

  // Actions
  setUser: (user: User | null) => void;
  setPermissions: (permissions: PermissionsResponse | null) => void;
  setLicenses: (licenses: TenantLicenses) => void;
  setLicense: (license: SessionLicense | null) => void;
  /** Spec 286 — a later call was refused for the licence: the SPA switches to the matching page. */
  licenceRefused: (refusal: LicenceRefusal) => void;
  /** The backend refused this person as archived: AuthProvider shows the notice instead of the app. */
  accountArchived: boolean;
  accountArchivedRefused: () => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  logout: () => void;
  updateActivity: () => void;
  
  // Permission checks
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyRole: (...roles: string[]) => boolean;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  // Initial state
  user: null,
  permissions: null,
  licenses: NO_LICENSES,
  license: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  lastActivityAt: Date.now(),

  // Actions
  setUser: (user) =>
    set({
      user,
      isAuthenticated: user !== null,
      error: null,
    }),

  setPermissions: (permissions) =>
    set({
      permissions,
    }),

  setLicenses: (licenses) =>
    set({
      licenses,
    }),

  setLicense: (license) => set({ license }),

  licenceRefused: (refusal) =>
    set((s) => ({
      // Nothing is licensed once a refusal arrives; the rest of what the session knew is kept.
      license: {
        ...(s.license ?? { ...UNVERIFIABLE_LICENSE, withheld: null }),
        state: refusal.state,
        plugReason: refusal.state === 'plugged' ? refusal.plugReason : null,
        governance: false, backup: false, ops: false,
      },
      licenses: NO_LICENSES,
    })),

  accountArchived: false,

  accountArchivedRefused: () =>
    set({
      accountArchived: true,
      user: null,
      permissions: null,
      licenses: NO_LICENSES,
      license: null,
      isAuthenticated: false,
    }),

  setLoading: (loading) =>
    set({
      isLoading: loading,
    }),

  setError: (error) =>
    set({
      error,
      isLoading: false,
    }),

  logout: () =>
    set({
      user: null,
      permissions: null,
      licenses: NO_LICENSES,
      license: null,
      isAuthenticated: false,
      error: null,
    }),

  updateActivity: () =>
    set({
      lastActivityAt: Date.now(),
    }),

  // Permission checks
  hasPermission: (permission: string) => {
    const { permissions } = get();
    if (!permissions) return false;

    // Check if permission exists in allPermissions array
    return permissions.allPermissions.includes(permission);
  },

  // Spec 285: SuperAdmin ⊇ Admin — a check Admin passes, SuperAdmin passes (roleSatisfies);
  // otherwise exact, case-insensitive. An unrecognised requested role matches nothing.
  hasRole: (role: string) => {
    const { user } = get();
    if (!user) return false;
    const wanted = toRole(role);
    return wanted !== null && roleSatisfies(user.role, [wanted]);
  },

  hasAnyRole: (...roles: string[]) => {
    const { user } = get();
    if (!user) return false;
    const wanted = roles.map(toRole).filter((r): r is Role => r !== null);
    return roleSatisfies(user.role, wanted);
  },
}));
